import { ReportBuilder, ReportBuilderProps, SQLEditor, SQLEditorProps, useQuill } from "@quillsql/react"
import { useState } from "react"

import { TabItem } from "@atoms"

import { TabStack } from "@molecules"

import { EDITABLE_SECTION_NAME } from "@pages/Reports/ReportDashboard/ReportDashboard.constants"
import { QuillButton, QuillDeleteButton } from "@pages/Reports/ReportDashboard/components/QuillButton/QuillButton"
import QuillDialog from "@pages/Reports/ReportDashboard/components/QuillDialog/QuillDialog"

import styles from "./ReportEditorDialog.module.scss"

type ReportEditorDialogProps = {
    isOpen: boolean
    onOpenChange: (open: boolean) => void
    dashboardId: string
    reportId?: string
}

export default function ReportEditorDialog({ isOpen, onOpenChange, dashboardId, reportId }: ReportEditorDialogProps) {
    const [activeTab, setActiveTab] = useState<"report-builder" | "sql-editor">("report-builder")
    const { loading, error } = useQuill(reportId)

    const isEditMode = !!reportId
    const dialogTitle = isEditMode ? "Edit Query" : "Add Report"

    const handleSubmit = () => {
        // Wait for 500 ms
        setTimeout(() => {
            onOpenChange(false)
        }, 500)
    }

    const commonProps: ReportBuilderProps & SQLEditorProps = {
        destinationDashboard: dashboardId,
        destinationSection: EDITABLE_SECTION_NAME,
        reportId: reportId,
        chartBuilderOptions: {
            showTableFormatOptions: true,
            showDashboardFilterFields: true,
        },
        onSubmitCreateReport: handleSubmit,
        onSubmitEditReport: handleSubmit,
        ButtonComponent: QuillButton,
        SecondaryButtonComponent: QuillButton,
        DeleteButtonComponent: QuillDeleteButton,
        ModalComponent: QuillDialog,
        ChartBuilderModalComponent: QuillDialog,
    }

    return (
        <QuillDialog title={dialogTitle} isOpen={isOpen} setIsOpen={onOpenChange}>
            <div className={styles.container}>
                {isEditMode && loading ? (
                    <div className={styles.loading}>Loading report...</div>
                ) : isEditMode && error ? (
                    <div className={styles.error}>Error loading report: {error}</div>
                ) : (
                    <>
                        <div className={styles.tabs}>
                            <TabStack variant="contained" size="md">
                                <TabItem
                                    isPressed={activeTab === "report-builder"}
                                    onClick={() => setActiveTab("report-builder")}
                                >
                                    Report Builder
                                </TabItem>
                                <TabItem
                                    isPressed={activeTab === "sql-editor"}
                                    onClick={() => setActiveTab("sql-editor")}
                                >
                                    SQL Editor
                                </TabItem>
                            </TabStack>
                        </div>
                        <div className={styles.content}>
                            {activeTab === "report-builder" ? (
                                <ReportBuilder {...commonProps} />
                            ) : (
                                <SQLEditor {...commonProps} />
                            )}
                        </div>
                    </>
                )}
            </div>
        </QuillDialog>
    )
}
